import React, { Component } from "react";
import { Link } from "react-router-dom";
import { Col, Row, Table } from "react-bootstrap";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import Sidebar from "../commonAdmin/Sidebar";
import Header from "../commonAdmin/Header";
import Footer from "../commonAdmin/Footer";
import Loader from "../../common/Loader";
import delivaApiService from "../../../common/services/apiService";
import {
  getDistanceUnit,
  getLocalStorage,
  getWeightUnit,
} from "../../../common/helpers/Utils";
import { ReactComponent as PayoutIcon } from "../../../assets/images/PayoutIconGreen.svg";
import { ReactComponent as CoinsIcon } from "../../../assets/images/Coins.svg";
import { ReactComponent as CoinVertical } from "../../../assets/images/CoinVertical.svg";
import { ReactComponent as PiggyBank } from "../../../assets/images/PiggyBank.svg";
import { ReactComponent as PackageIcon } from "../../../assets/images/Package.svg";
import { ReactComponent as FlagCheckeredGreen } from "../../../assets/images/FlagCheckeredGreen.svg";
import moment from "moment";
import "./PromoPricingDetails.scss";
import CaretDownArrow from "../../../assets/images/CaretDoubleDown.svg";
import { connect } from "react-redux";
import { actionFetchCountries } from "../../../common/redux/actions/actionCountry";

const REQUEST_TYPES = [
  { value: "LOCAL", label: "Local" },
  { value: "INTERSTATE", label: "Interstate" },
  { value: "INTERNATIONAL", label: "International" },
];

const DURATION_OPTIONS = [
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
  { value: "YEARLY", label: "Yearly" },
  { value: "LIFETIME", label: "Lifetime" },
];

class PromoPricingDetails extends Component {
  constructor(props) {
    super(props);

    const shouldCollapse = Boolean(
      this.props.location?.state?.collapseSections,
    );

    this.state = {
      loading: false,
      activeTab: "overview", // overview | analytics
      openSections: {
        general: true,
        eligibility: !shouldCollapse,
        pricing: !shouldCollapse,
      },
      detail: null,
      analytics: null,

      // User List Pagination (Overview Tab)
      userList: [],
      pageno: 1,
      records: 5,
      inputRecords: 5,

      // Analytics Detailed Table Pagination
      analyticsList: [],
      analyticsPageNo: 1,
      analyticsRecords: 5,
      analyticsInputRecords: 5,
    };
  }

  componentDidMount() {
    if (!getLocalStorage("adminInfo")) {
      this.props.history.push("/login");
      return;
    }

    if (this.props.location?.state?.collapseSections) {
      this.setState({
        openSections: {
          general: true,
          eligibility: false,
          pricing: false,
        },
      });
    }

    const { id } = this.props.match.params;
    this.loadDetail(id);
    this.loadAnalytics(id);
  }

  loadDetail(id) {
    this.setState({ loading: true });
    delivaApiService("getPromoPricingById", { id })
      .then((res) => {
        const data = res && res.data && res.data.resourceData;
        const users = data?.user_list || data?.redemptions || [];
        this.setState({
          detail: data || null,
          userList: Array.isArray(users) ? users : [],
          loading: false,
        });
      })
      .catch(() => this.setState({ loading: false }));
  }

  loadAnalytics(id) {
    delivaApiService("getPromoPricingAnalytics", { id })
      .then((res) => {
        const data = res && res.data && res.data.resourceData;
        if (data) {
          const tableData = data.daily_breakdown || [];

          this.setState({
            analytics: data,
            analyticsList: Array.isArray(tableData) ? tableData : [],
            analyticsPageNo: 1,
          });
        }
      })
      .catch(() => {});
  }

  getPageNumbers(currentPage, totalPages) {
    if (!totalPages || totalPages <= 0) return [1];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = start + maxVisible - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - maxVisible + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, idx) => start + idx);
  }

  toggleSection = (key) => {
    this.setState((prev) => ({
      openSections: { ...prev.openSections, [key]: !prev.openSections[key] },
    }));
  };

  onChangePage(page, totalPages) {
    if (page < 1 || page > totalPages) return;
    this.setState({ pageno: page });
  }

  onChangeAnalyticsPage(page, totalPages) {
    if (page < 1 || page > totalPages) return;
    this.setState({ analyticsPageNo: page });
  }

  fmt(val) {
    return val !== null && val !== undefined && val !== "" ? val : "--";
  }

  fmtDate(val) {
    return val ? moment(val).format("DD MMM YYYY") : "--";
  }

  fmtTime(val) {
    return val ? moment(val).format("hh:mm A") : "--";
  }

  fmtBool(val) {
    const isYes = val === true || val === "yes" || val === "true";
    return <span>{isYes ? "Yes" : "No"}</span>;
  }

  fmtStatus(val) {
    const isYes = val === true || val === "yes" || val === "true";
    return <span>{isYes ? "Active" : "Inactive"}</span>;
  }

  fmtArray(val) {
    if (Array.isArray(val) && val.length > 0) {
      return val
        .map((item) => {
          const text = String(item);
          return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
        })
        .join(", ")
        .replaceAll(/_/g, " ");
    }
    if (typeof val === "string" && val.trim()) {
      return val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
    }
    return "--";
  }

  getCurrencySymbol() {
    const code = this.state.detail?.currency;
    if (!code) return "₦";
    const FALLBACK = {
      NGN: "₦",
      USD: "$",
      EUR: "€",
      GBP: "£",
      ZAR: "R",
      KES: "KSh",
      GHS: "₵",
    };
    return FALLBACK[code.toUpperCase()] || code;
  }

  renderField(label, value, colMd = 2) {
    return (
      <Col md={colMd} className="mb-3">
        <p className="ppd-field-label">{label}</p>
        <p className="ppd-field-value">{value || "--"}</p>
      </Col>
    );
  }

  renderGeneralInfo() {
    const { detail: d } = this.state;
    if (!d) return null;
    return (
      <div className="ppd-accordion-body">
        <Row className="mb-2">
          {this.renderField("Promotion Name", this.fmt(d.name), 4)}
          {this.renderField("Description", this.fmt(d.description), 8)}
        </Row>
        <Row>
          {this.renderField("Status", this.fmtStatus(d.is_active), 2)}
          {this.renderField("Start Date", this.fmtDate(d.start_date), 2)}
          {this.renderField("Start Time", this.fmtTime(d.start_date), 2)}
          {this.renderField("End Date", this.fmtDate(d.end_date), 2)}
          {this.renderField("End Time", this.fmtTime(d.end_date), 2)}
          {this.renderField("Currency", this.fmt(d.currency), 2)}
        </Row>
      </div>
    );
  }

  renderEligibility() {
    const { detail: d } = this.state;
    if (!d) return null;
    const isLocal = d.request_types === "LOCAL";
    const symbol = this.getCurrencySymbol();
    const typeLabel =
      REQUEST_TYPES.find((item) => item.value === d.request_types)?.label ||
      d.request_types ||
      "--";

    return (
      <div className="ppd-accordion-body">
        <Row>
          {this.renderField("Delivery Type", typeLabel, 3)}
          {this.renderField("Country", this.fmt(d.countries), 3)}
          {this.renderField(
            isLocal ? "State" : "Origin State",
            this.fmt(d.origin_states),
            3,
          )}
          {this.renderField(
            isLocal ? "City" : "Origin City",
            this.fmtArray(d.origin_cities),
            3,
          )}
        </Row>

        <Row>
          {this.renderField("Origin Location", this.fmt(d.origin_location), 3)}
          {!isLocal &&
            this.renderField("Destination State", this.fmt(d.dest_states), 3)}
          {!isLocal &&
            this.renderField(
              "Destination City",
              this.fmtArray(d.dest_cities),
              3,
            )}
          {this.renderField(
            "Destination Location",
            this.fmt(d.dest_location),
            3,
          )}
        </Row>

        <Row>
          {this.renderField(
            "Distance Range",
            d.min_distance_km != null || d.max_distance_km != null
              ? `${d.min_distance_km ?? "--"} / ${d.max_distance_km ?? "--"} ${getDistanceUnit(d.currency, this.props.countries).toLocaleLowerCase()}`
              : "--",
            3,
          )}
          {this.renderField(
            "Max Recommended Price",
            d.max_recommended_price != null
              ? `${symbol} ${Number(d.max_recommended_price).toLocaleString()}`
              : "--",
            3,
          )}
          {this.renderField(
            "Weight Range",
            d.min_weight_kg != null || d.max_weight_kg != null
              ? `${d.min_weight_kg ?? "--"} / ${d.max_weight_kg ?? "--"} ${getWeightUnit(d.currency, this.props.countries).toLocaleLowerCase()}`
              : "--",
            3,
          )}
          {this.renderField(
            "Maximum Package Value",
            d.max_package_value != null
              ? `${symbol} ${Number(d.max_package_value).toLocaleString()}`
              : "--",
            3,
          )}
        </Row>

        <Row>
          {this.renderField("Vehicle Types", this.fmtArray(d.vehicle_types), 6)}
          {this.renderField(
            "Delivery Mode",
            d.delivery_modes === "DOOR_TO_DOOR"
              ? "Door to Door"
              : d.delivery_modes === "PARK_TO_PARK"
                ? "Park to Park"
                : this.fmt(d.delivery_modes),
            6,
          )}
        </Row>
      </div>
    );
  }

  renderSenderPricing() {
    const { detail: d } = this.state;
    if (!d) return null;
    const symbol = this.getCurrencySymbol();
    const usage = d.usage || {};

    const senderPricingSummary = () => {
      switch (d.sender_pricing_mode) {
        case "FLAT":
          return `Fixed Price${
            d.sender_flat_amount != null
              ? ` (${symbol}${Number(d.sender_flat_amount).toLocaleString()})`
              : ""
          }`;
        case "DISCOUNT_PERCENT":
          return `Percentage Discount${
            d.sender_discount_pct != null ? ` (${d.sender_discount_pct}%)` : ""
          }`;
        case "DISCOUNT_FIXED":
          return `Fixed Discount Amount${
            d.sender_discount_amt != null
              ? ` (${symbol}${Number(d.sender_discount_amt).toLocaleString()})`
              : ""
          }`;
        case "CAPPED":
          return `Maximum Price Cap${
            d.sender_cap_amount != null
              ? ` (${symbol}${Number(d.sender_cap_amount).toLocaleString()})`
              : ""
          }`;
        default:
          return this.fmt(d.sender_pricing_mode);
      }
    };

    const partnerPricingSummary = () => {
      switch (d.partner_pricing_mode) {
        case "FIXED_RECOMMENDED_PRICE":
          return "Fixed Recommended Price";
        case "FLAT":
          return `Fixed Amount${
            d.partner_flat_amount != null
              ? ` (${symbol}${Number(d.partner_flat_amount).toLocaleString()})`
              : ""
          }`;
        case "PERCENT_OF_SENDER":
          return `Percentage of Sender Price${
            d.partner_pct_of_sender != null
              ? ` (${d.partner_pct_of_sender}%)`
              : ""
          }`;
        case "STANDARD":
          return "Standard Platform Commission";
        default:
          return this.fmt(d.partner_pricing_mode);
      }
    };

    const totalBudget = usage.total_budget ?? d.total_budget;
    const duration = usage.duration ?? d.duration;
    const durationLabel =
      DURATION_OPTIONS.find((opt) => opt.value === duration)?.label ||
      duration ||
      "--";
    const totalUsageLimit = usage.total_usage_limit ?? d.total_usage_limit;
    const perUserLimit = usage.per_user_limit ?? d.per_user_limit;

    return (
      <div className="ppd-accordion-body">
        <Row className="mb-3">
          {this.renderField("Pricing Method", senderPricingSummary(), 3)}
          {this.renderField(
            "Floor Amount",
            d.sender_floor_amount != null
              ? `${symbol}${Number(d.sender_floor_amount).toLocaleString()}`
              : "--",
            3,
          )}
          {this.renderField("Partner Pricing", partnerPricingSummary(), 3)}
        </Row>

        <hr className="my-2" />

        <p className="ppd-field-value ppd-heading mb-2 mt-3">
          <span>Usage Controls</span>
        </p>
        <Row className="mb-3">
          {this.renderField(
            "Total Budget",
            totalBudget != null
              ? `${symbol}${Number(totalBudget).toLocaleString()}`
              : "--",
            3,
          )}
          {this.renderField("Duration", durationLabel, 3)}
          {this.renderField(
            "Maximum Total Uses",
            totalUsageLimit != null
              ? Number(totalUsageLimit).toLocaleString()
              : "--",
            3,
          )}
          {this.renderField(
            "Maximum Uses Per Sender",
            perUserLimit != null ? Number(perUserLimit).toLocaleString() : "--",
            3,
          )}
        </Row>

        <hr className="my-2" />

        <p className="ppd-field-value ppd-heading mb-2 mt-3">
          <span>Display Settings</span>
        </p>
        <Row className="mb-3">
          {this.renderField(
            "Allow Promo Stacking",
            this.fmtBool(d.stackable_with_promo_codes),
            3,
          )}
          {this.renderField(
            "Show Max Bid",
            this.fmtBool(d.show_partner_bid_ceiling),
            3,
          )}
        </Row>
        <Row>
          {this.renderField(
            "Promotional Message",
            this.fmt(d.promo_message),
            12,
          )}
        </Row>
        <Row>
          {this.renderField("Partner Message", this.fmt(d.partner_message), 12)}
        </Row>
        <Row>
          {this.renderField(
            "Terms & Conditions Link",
            this.fmt(d.terms_url),
            12,
          )}
        </Row>
      </div>
    );
  }

  renderAnalytics() {
    const {
      analytics: a,
      analyticsList,
      analyticsPageNo,
      analyticsRecords,
      analyticsInputRecords,
    } = this.state;

    const symbol = this.getCurrencySymbol();
    const formatCurrency = (val) =>
      val != null ? `${symbol} ${Number(val).toLocaleString()}` : `${symbol} 0`;

    const totalRedemption = a?.redemption_count ?? 0;
    const promoSpend = a?.subsidy_spend ?? 0;
    const remainingBudget = a?.remaining_budget ?? 0;

    const avgSavings =
      totalRedemption > 0 ? Math.round(promoSpend / totalRedemption) : 0;

    const citiesList =
      a?.geographic_distribution
        ?.filter((item) => item.city && item.city.toUpperCase() !== "UNKNOWN")
        ?.map((item) => item.city)
        ?.join(", ") || "--";

    const chartData =
      a?.daily_breakdown?.map((item) => ({
        date: item.date ? moment(item.date).format("D MMM") : "--",
        rawDate: item.date,
        usage: item.count || 0,
        spend: item.spend || 0,
      })) || [];

    const cardContainerStyle = {
      background: "#fff",
      borderRadius: "12px",
      padding: "16px 20px",
      display: "flex",
      alignItems: "center",
      gap: "14px",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
      height: "100%",
    };

    const iconCircleStyle = {
      width: "56px",
      height: "56px",
      borderRadius: "50%",
      backgroundColor: "#F4F4F4",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#085133",
      flexShrink: 0,
    };

    const pageSize =
      Number(analyticsRecords) > 0 ? Number(analyticsRecords) : 5;
    const totalRecords = analyticsList.length;
    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
    const currentPage = Math.min(Math.max(1, analyticsPageNo), totalPages);

    const start = (currentPage - 1) * pageSize;
    const pageItems = analyticsList.slice(start, start + pageSize);
    const pagesArray = this.getPageNumbers(currentPage, totalPages);

    return (
      <div className="mt-2">
        {/* KPI Stat Cards */}
        <Row className="g-3 mb-2">
          <Col lg={3} md={6} className="mb-3">
            <div style={cardContainerStyle}>
              <div style={iconCircleStyle}>
                <PayoutIcon style={{ width: "24px", height: "24px" }} />
              </div>
              <div>
                <h4
                  className="mb-0 font-weight-medium"
                  style={{ fontSize: "18px", color: "var(--gray-600)" }}
                >
                  {totalRedemption.toLocaleString()}
                </h4>
                <span style={{ fontSize: "18px", color: "#555555" }}>
                  Total Redemption
                </span>
              </div>
            </div>
          </Col>

          <Col lg={3} md={6} className="mb-3">
            <div style={cardContainerStyle}>
              <div style={iconCircleStyle}>
                <CoinsIcon style={{ width: "24px", height: "24px" }} />
              </div>
              <div>
                <h4
                  className="mb-0 font-weight-medium"
                  style={{ fontSize: "18px", color: "var(--gray-600)" }}
                >
                  {formatCurrency(promoSpend)}
                </h4>
                <span style={{ fontSize: "18px", color: "#555555" }}>
                  Promo Spend
                </span>
              </div>
            </div>
          </Col>

          <Col lg={3} md={6} className="mb-3">
            <div style={cardContainerStyle}>
              <div style={iconCircleStyle}>
                <CoinVertical style={{ width: "24px", height: "24px" }} />
              </div>
              <div>
                <h4
                  className="mb-0 font-weight-medium"
                  style={{ fontSize: "18px", color: "var(--gray-600)" }}
                >
                  {formatCurrency(remainingBudget)}
                </h4>
                <span style={{ fontSize: "18px", color: "#555555" }}>
                  Remaining Budget
                </span>
              </div>
            </div>
          </Col>

          <Col lg={3} md={6} className="mb-3">
            <div style={cardContainerStyle}>
              <div style={iconCircleStyle}>
                <PiggyBank style={{ width: "24px", height: "24px" }} />
              </div>
              <div>
                <h4
                  className="mb-0 font-weight-medium"
                  style={{ fontSize: "18px", color: "var(--gray-600)" }}
                >
                  {formatCurrency(avgSavings)}
                </h4>
                <span style={{ fontSize: "18px", color: "#555555" }}>
                  Average Savings
                </span>
              </div>
            </div>
          </Col>
        </Row>

        {/* Top Cities */}
        <Row className="g-3 mb-2">
          <Col md={6} className="mb-3">
            <div style={cardContainerStyle}>
              <div style={iconCircleStyle}>
                <PackageIcon style={{ width: "24px", height: "24px" }} />
              </div>
              <div>
                <span
                  style={{
                    fontSize: "18px",
                    fontWeight: "normal",
                    color: "#555555",
                  }}
                >
                  Top origin cities
                </span>
                <h5
                  className="mb-0 font-weight-medium"
                  style={{ fontSize: "18px", color: "var(--gray-600)" }}
                >
                  {citiesList}
                </h5>
              </div>
            </div>
          </Col>

          <Col md={6} className="mb-3">
            <div style={cardContainerStyle}>
              <div style={iconCircleStyle}>
                <FlagCheckeredGreen style={{ width: "24px", height: "24px" }} />
              </div>
              <div>
                <span
                  style={{
                    fontSize: "18px",
                    fontWeight: "normal",
                    color: "#555555",
                  }}
                >
                  Top Destination Cities
                </span>
                <h5
                  className="mb-0 font-weight-medium"
                  style={{ fontSize: "18px", color: "var(--gray-600)" }}
                >
                  {citiesList}
                </h5>
              </div>
            </div>
          </Col>
        </Row>

        {/* Charts */}
        <Row className="g-3">
          <Col md={6} className="mb-4">
            <div
              style={{
                background: "#fff",
                borderRadius: "12px",
                padding: "20px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
              }}
            >
              <h5
                className="font-weight-normal mb-4"
                style={{ fontSize: "24px", color: "var(--gray-600)" }}
              >
                Daily Promotion Usage
              </h5>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="usageGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="#10B981"
                        stopOpacity={0.35}
                      />
                      <stop
                        offset="95%"
                        stopColor="#10B981"
                        stopOpacity={0.0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="0"
                    vertical={true}
                    horizontal={false}
                    stroke="#F3F4F6"
                  />
                  <XAxis
                    dataKey="date"
                    axisLine={{ stroke: "#E5E7EB" }}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: "#6B7280" }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: "#6B7280" }}
                  />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="usage"
                    stroke="#10B981"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#usageGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Col>

          <Col md={6} className="mb-4">
            <div
              style={{
                background: "#fff",
                borderRadius: "12px",
                padding: "20px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
              }}
            >
              <h5
                className="font-weight-normal mb-4"
                style={{ fontSize: "24px", color: "var(--gray-600)" }}
              >
                Daily Promotional Spend
              </h5>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="0"
                    vertical={false}
                    horizontal={true}
                    stroke="#F3F4F6"
                  />
                  <XAxis
                    dataKey="date"
                    axisLine={{ stroke: "#E5E7EB" }}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: "#6B7280" }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: "#6B7280" }}
                    tickFormatter={(val) => `${val / 1000}k`}
                  />
                  <Tooltip
                    formatter={(val) => [
                      `${symbol}${val.toLocaleString()}`,
                      "Spend",
                    ]}
                  />
                  <Bar
                    dataKey="spend"
                    fill="#005B36"
                    barSize={16}
                    radius={[2, 2, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Col>
        </Row>

        {/* Detailed Table */}
        <h3 className="ppd-user-list-title line-ht text-nowrap mt-0">
          Detailed Table <span className="color-grey">({totalRecords})</span>
        </h3>
        <div className="row">
          <div className="col-sm-12 order-boxlist">
            <Table responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Usage</th>
                  <th>Spend</th>
                </tr>
              </thead>
              <tbody className="ttl-order-table">
                {pageItems.length > 0 ? (
                  pageItems.map((item, idx) => (
                    <tr key={idx}>
                      <td>{start + idx + 1}</td>
                      <td>
                        {item.date
                          ? moment(item.date).format("DD MMM YYYY")
                          : "--"}
                      </td>
                      <td>{item.count ?? "--"}</td>
                      <td>
                        {item.spend != null
                          ? `${symbol}${Number(item.spend).toLocaleString()}`
                          : "--"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center">
                      No Records Available
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {totalRecords > 0 && (
              <nav aria-label="Page navigation">
                <ul className="pagination pg-blue deliva-pagination justify-content-end">
                  <li className="page-item">
                    <button
                      className="page-link rotate-180 control-btn"
                      onClick={() =>
                        this.onChangeAnalyticsPage(currentPage - 1, totalPages)
                      }
                      disabled={currentPage <= 1}
                    >
                      <span className="icon-next"></span>
                      <span className="sr-only">Previous</span>
                    </button>
                  </li>
                  {pagesArray.map((page) => (
                    <li key={page} className="page-item">
                      <a
                        className={`page-link ${currentPage === page ? "active" : ""}`}
                        onClick={() =>
                          this.onChangeAnalyticsPage(page, totalPages)
                        }
                      >
                        {String(page).padStart(2, "0")}
                      </a>
                    </li>
                  ))}
                  <li className="page-item">
                    <button
                      className="page-link control-btn"
                      onClick={() =>
                        this.onChangeAnalyticsPage(currentPage + 1, totalPages)
                      }
                      disabled={currentPage >= totalPages}
                    >
                      <span className="icon-next"></span>
                      <span className="sr-only">Next</span>
                    </button>
                  </li>
                  <li className="page-value">
                    Enter Per Page{" "}
                    <input
                      type="number"
                      value={analyticsInputRecords}
                      onChange={(e) =>
                        this.setState({ analyticsInputRecords: e.target.value })
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const val = parseInt(
                            this.state.analyticsInputRecords,
                            10,
                          );
                          if (val > 0) {
                            this.setState({
                              analyticsRecords: val,
                              analyticsPageNo: 1,
                            });
                          }
                        }
                      }}
                    />
                  </li>
                </ul>
              </nav>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderUserList() {
    const { userList, pageno, records, inputRecords } = this.state;

    const pageSize = Number(records) > 0 ? Number(records) : 5;
    const totalRecords = userList.length;
    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
    const currentPage = Math.min(Math.max(1, pageno), totalPages);

    const start = (currentPage - 1) * pageSize;
    const pageItems = userList.slice(start, start + pageSize);
    const pagesArray = this.getPageNumbers(currentPage, totalPages);

    return (
      <>
        <h3 className="ppd-user-list-title line-ht text-nowrap">
          List of Users <span className="color-grey">({totalRecords})</span>
        </h3>
        <div className="row">
          <div className="col-sm-12 order-boxlist">
            <Table responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Sender Name</th>
                  <th>Email ID</th>
                  <th>Date of Use</th>
                  <th>Order ID</th>
                </tr>
              </thead>
              <tbody className="ttl-order-table">
                {pageItems.length > 0 ? (
                  pageItems.map((u, idx) => {
                    const orderId = u.order_id || u.orderId;
                    const dateValue = u.date_of_use || u.dateOfUse || u.date;
                    const senderName =
                      u.sender_name || u.senderName || u.name || "--";
                    const email = u.email_id || u.email || "--";

                    return (
                      <tr key={idx}>
                        <td>{start + idx + 1}</td>
                        <td>{senderName}</td>
                        <td>{email}</td>
                        <td>
                          {dateValue
                            ? moment(dateValue).format("DD MMM YYYY")
                            : "--"}
                        </td>
                        <td>
                          {orderId ? (
                            <Link
                              to={`/billing/orderDetails/${orderId}`}
                              className="link"
                            >
                              {`#${orderId}`}
                            </Link>
                          ) : (
                            "--"
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center">
                      No Records Available
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {totalRecords > 0 && (
              <nav aria-label="Page navigation">
                <ul className="pagination pg-blue deliva-pagination justify-content-end">
                  <li className="page-item">
                    <button
                      className="page-link rotate-180 control-btn"
                      onClick={() =>
                        this.onChangePage(currentPage - 1, totalPages)
                      }
                      disabled={currentPage <= 1}
                    >
                      <span className="icon-next"></span>
                      <span className="sr-only">Previous</span>
                    </button>
                  </li>
                  {pagesArray.map((page) => (
                    <li key={page} className="page-item">
                      <a
                        className={`page-link ${currentPage === page ? "active" : ""}`}
                        onClick={() => this.onChangePage(page, totalPages)}
                      >
                        {String(page).padStart(2, "0")}
                      </a>
                    </li>
                  ))}
                  <li className="page-item">
                    <button
                      className="page-link control-btn"
                      onClick={() =>
                        this.onChangePage(currentPage + 1, totalPages)
                      }
                      disabled={currentPage >= totalPages}
                    >
                      <span className="icon-next"></span>
                      <span className="sr-only">Next</span>
                    </button>
                  </li>
                  <li className="page-value">
                    Enter Per Page{" "}
                    <input
                      type="number"
                      value={inputRecords}
                      onChange={(e) =>
                        this.setState({ inputRecords: e.target.value })
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const val = parseInt(this.state.inputRecords, 10);
                          if (val > 0) {
                            this.setState({ records: val, pageno: 1 });
                          }
                        }
                      }}
                    />
                  </li>
                </ul>
              </nav>
            )}
          </div>
        </div>
      </>
    );
  }

  render() {
    const { loading, detail, activeTab, openSections } = this.state;

    return (
      <div className="page__wrapper h-100">
        <Header {...this.props} />
        <div className="mainContainer CustomerTable">
          <Row noGutters className="h-100">
            <Sidebar {...this.props} />
            <Col md={9} lg={10} className="boxTyp3">
              <div className="container-fluid">
                <div className="content-wrapper">
                  <Loader showLoader={loading} />

                  <h3 className="font-weight-normal mb-0 line-ht color-33 fs-24">
                    Promo details
                  </h3>
                  <h5 className="font-weight-normal mb-2 line-ht color-head">
                    <span
                      className="pointer"
                      onClick={() =>
                        this.props.history.push("/promoPricingList")
                      }
                    >
                      List of promo
                    </span>
                    <span className="icon-next mx-2 fs12"></span>
                    <span>Promo details</span>
                  </h5>

                  <div className="ppd-tabs">
                    <div
                      className={`ppd-tab${activeTab === "overview" ? " active" : ""} left-button`}
                      onClick={() => this.setState({ activeTab: "overview" })}
                    >
                      Overview
                    </div>
                    <div
                      className={`ppd-tab${activeTab === "analytics" ? " active" : ""} right-button`}
                      onClick={() => this.setState({ activeTab: "analytics" })}
                    >
                      Analytics
                    </div>
                  </div>

                  {activeTab === "overview" && detail && (
                    <>
                      <div className="ppd-accordion">
                        <div
                          className="ppd-accordion-header"
                          onClick={() => this.toggleSection("general")}
                        >
                          <span>General Information</span>
                          <span
                            className={`ppd-chevron${openSections.general ? " open" : ""}`}
                          >
                            <img
                              src={CaretDownArrow}
                              alt="toggle"
                              className={`ppd-caret-img ${openSections.general ? "open" : ""}`}
                            />
                          </span>
                        </div>
                        {openSections.general && this.renderGeneralInfo()}
                      </div>

                      <div className="ppd-accordion">
                        <div
                          className="ppd-accordion-header"
                          onClick={() => this.toggleSection("eligibility")}
                        >
                          <span>Eligibility Criteria</span>
                          <span
                            className={`ppd-chevron${openSections.eligibility ? " open" : ""}`}
                          >
                            <img
                              src={CaretDownArrow}
                              alt="toggle"
                              className={`ppd-caret-img ${openSections.eligibility ? "open" : ""}`}
                            />
                          </span>
                        </div>
                        {openSections.eligibility && this.renderEligibility()}
                      </div>

                      <div className="ppd-accordion">
                        <div
                          className="ppd-accordion-header"
                          onClick={() => this.toggleSection("pricing")}
                        >
                          <span>Sender Pricing</span>
                          <span
                            className={`ppd-chevron${openSections.pricing ? " open" : ""}`}
                          >
                            <img
                              src={CaretDownArrow}
                              alt="toggle"
                              className={`ppd-caret-img ${openSections.pricing ? "open" : ""}`}
                            />
                          </span>
                        </div>
                        {openSections.pricing && this.renderSenderPricing()}
                      </div>

                      {this.renderUserList()}
                    </>
                  )}

                  {activeTab === "analytics" && this.renderAnalytics()}
                </div>
              </div>
              <Footer />
            </Col>
          </Row>
        </div>
      </div>
    );
  }
}

export default connect(
  (state) => ({ countries: state.country.countries }),
  (dispatch) => ({ fetchCountries: () => dispatch(actionFetchCountries()) }),
)(PromoPricingDetails);
